import { Button, Item, ItemActions, ItemContent, Password } from '@ibsys/kit';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  PasswordRulesHint,
  User,
  useEnglishLayoutInput,
  usePasswordRulesField,
  usePasswordStrengthValidator,
  useUpdateUserPassword,
} from '@/entities/users';

type Props = {
  className?: string;
  user: User;
};

type FormValues = {
  password: string;
};

export const UserResetPassword: FC<Props> = (props) => {
  const { className, user } = props;

  const { t } = useTranslation(['common', 'users']);

  const { control, handleSubmit, reset, setError, clearErrors } = useForm<FormValues>({
    defaultValues: { password: '' },
  });

  const checkPasswordStrength = usePasswordStrengthValidator();
  const { showServerRefusal } = usePasswordRulesField({ control, setError, clearErrors, name: 'password' });

  const englishLayoutInput = useEnglishLayoutInput();

  const { isPending, mutateAsync: resetPassword } = useUpdateUserPassword();

  const submit = handleSubmit(async ({ password }) => {
    try {
      await resetPassword({
        id: user.id,
        body: {
          password,
        },
      });
      reset();
    } catch (error) {
      // Введённый пароль остаётся для исправления
      showServerRefusal(error);
    }
  });

  return (
    <Item variant={'outline'} className={className}>
      <ItemContent>
        <Password
          {...englishLayoutInput}
          control={control}
          name={'password'}
          label={
            <>
              {t('users:setNewPassword')}
              <PasswordRulesHint />
            </>
          }
          autoComplete={'new-password'}
          rules={{
            required: { value: true, message: t('common:required') },
            // Владелец пароля — открытый пользователь. Его текущий пароль форме не известен — его сверяет сервер
            validate: (password) =>
              checkPasswordStrength(password, {
                email: user.email,
                firstName: user.firstName,
                lastName: user.lastName,
                middleName: user.middleName,
              }),
          }}
        />
      </ItemContent>
      <ItemActions className={'mt-auto'}>
        <Button variant={'secondary'} isLoading={isPending} onClick={submit}>
          {t('common:save')}
        </Button>
      </ItemActions>
    </Item>
  );
};
